/**
 * `<ScreenList>` — the screens, as a column, beside the toolbar key that opens it.
 *
 * The same catalog the tab strip draws, in the shape a side popover wants. Not
 * `ScreenTabs` reused: that is a two-row measured strip whose whole design is
 * about fitting names across a width, and a panel hanging off a 34px rail has
 * height to spend and no width to measure.
 *
 * **It switches and nothing else.** Renaming, deleting, adding and duplicating
 * all live on the strip inside the editor, where the screen being changed is the
 * one on screen. A popover that also edited would be a second place to do the
 * same things, differing only in which of them it left out.
 */

import { isClub, isOwn, orderTabs, type Screen } from "../lib/screens";

export interface ScreenListProps {
  screens: readonly Screen[];
  /** The one showing, so it can be marked rather than merely listed. */
  selected?: string;
  onSelect: (key: string) => void;
  /**
   * Make a TopDown over the screen showing (`docs/features/topdown.md`), when
   * that screen is an ordinary one. provisional — owner's call: the mock shows
   * where a TopDown is listed, not where one is made, so this is a row under
   * Special rather than a control anywhere that would need counting.
   */
  onNewTopDown?: () => void;
  /**
   * Make a Seasonality screen over the screen showing
   * (`docs/features/seasonality.md`). Absent without `ind:Seasonality` (Basic) or
   * `ind:SeasonalityPro` — hidden, not locked (owner, 2026-10-22).
   */
  onNewSeasonality?: () => void;
  /** Whether Seasonality screens are listed at all — the same capability rule. */
  showSeasonality?: boolean;
  /** Pointer handlers, so hovering the panel does not close it. */
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

export function ScreenList({
  screens,
  selected,
  onSelect,
  onNewTopDown,
  onNewSeasonality,
  showSeasonality = false,
  onPointerEnter,
  onPointerLeave,
}: ScreenListProps) {
  // Hidden, not locked: without the capability a Seasonality screen is not listed.
  const tabs = orderTabs(screens).filter((screen) => showSeasonality || !screen.seasonality);
  // A JP trade-list screen is not a screen of rows you configure but a list the
  // group computes (`docs/plans/jp-recs.md`), so it sits apart, under Special,
  // in the order the strip has it.
  // A TopDown is there too: one entry, its Symbols grid another screen.
  const isSpecial = (screen: Screen) => Boolean(screen.recs || screen.topDown || screen.seasonality);
  const ordinary = tabs.filter((screen) => !isSpecial(screen));
  const special = tabs.filter(isSpecial);
  const row = (screen: Screen) => {
    const active = screen.key === selected;
    return (
      <button
        key={screen.key}
        type="button"
        role="option"
        aria-selected={active}
        onClick={() => onSelect(screen.key)}
        title={
          isClub(screen)
            ? `${screen.name} — ${screen.ownerKey}'s screen`
            : `Show ${screen.name}`
        }
        style={active ? { ...ROW, ...ROW_ON } : ROW}
      >
        {/* A lock reads as "you cannot edit this half", which is exactly
            what a club screen means here — and it is the one fact that
            changes what the editor will let you do once you have picked
            it. */}
        {screen.topDown && (
          <span aria-hidden style={LOCK}>
            ▦
          </span>
        )}
        {screen.seasonality && (
          <span aria-hidden style={LOCK}>
            📅
          </span>
        )}
        {isClub(screen) && (
          <span aria-hidden style={LOCK}>
            🔒
          </span>
        )}
        <span style={NAME}>{screen.name}</span>
        {isClub(screen) && screen.ownerKey && (
          <span style={CLUB}>{screen.ownerKey}</span>
        )}
        {isOwn(screen) && active && <span style={MARK}>●</span>}
      </button>
    );
  };
  return (
    <div
      role="listbox"
      aria-label="Screens"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={PANEL}
    >
      {tabs.length === 0 ? (
        <div style={EMPTY}>No screens yet.</div>
      ) : (
        <>
          {ordinary.map(row)}
          {(special.length > 0 || onNewTopDown || onNewSeasonality) && (
            <>
              <div style={HEADING} role="presentation">
                Special
              </div>
              {special.map(row)}
              {onNewTopDown && (
                <button type="button" onClick={onNewTopDown} style={{ ...ROW, ...NEW }}
                  title="A Sector → Industry → Symbols screen over the screen showing">
                  ＋ TopDown over this screen
                </button>
              )}
              {onNewSeasonality && (
                <button type="button" onClick={onNewSeasonality} style={{ ...ROW, ...NEW }}
                  title="Seasonal windows — when each symbol has historically moved — over the screen showing">
                  ＋ Seasonality over this screen
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 160,
  maxWidth: 260,
  maxHeight: "60vh",
  overflowY: "auto",
  padding: 4,
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  font: "inherit",
  fontSize: 12,
  textAlign: "left",
  padding: "4px 8px",
  borderRadius: 4,
  border: "1px solid transparent",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const ROW_ON: React.CSSProperties = {
  background: "var(--qf-background)",
  borderColor: "var(--qf-border)",
  color: "var(--qf-text)",
  fontWeight: 600,
};

const NAME: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis" };
const LOCK: React.CSSProperties = { fontSize: 9, color: "var(--qf-text-muted)" };
const MARK: React.CSSProperties = { marginLeft: "auto", fontSize: 8, color: "var(--qf-accent)" };
const CLUB: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: 9,
  letterSpacing: "0.04em",
  padding: "1px 4px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
};
const NEW: React.CSSProperties = { color: "var(--qf-text-muted)", fontStyle: "italic" };
const HEADING: React.CSSProperties = {
  padding: "6px 8px 2px",
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};
const EMPTY: React.CSSProperties = {
  padding: "6px 8px",
  fontSize: 12,
  color: "var(--qf-text-muted)",
};
